import * as Helpers from "./helpers";
import { useAppDispatch, useAppSelector } from "@portal/redux/store";
import { useRouter } from "next/navigation";
import { MainBox } from "../components/mainBox";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { CalculateStartUpAmountForFund, MapFundsToStartUpAgreement } from "../completesaving/helpers/GetStartUpAgreementRequest";
import { GetEndDateForStartUpAgreement } from "@portal/helpers";
import useCustomerInfo from "@portal/hooks/useCustomerInfo";
import { InfoBoxComponent } from "@portal/components/atoms/infoBox";
import useSubmitCreateAgreement from "@portal/hooks/useSubmitCreateAgreement";
import { useMemo, useState } from "react";
import { setResponse } from "@portal/redux/reducers/ui/createSavingsAgreement";
import { Footer } from "./components";
import { AgreementSummary, SuitabilityReport } from "../finalpage/components";
import { format } from "date-fns";
import useSubmitOrderFund from "@portal/hooks/useSubmitOrderFund";

export const SuitabilityReportPage = ({ centerId, isDefaultLanguage }: IPageProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const savingCalculations = useAppSelector(state => state.persisted.savingCalculations.request);
  const { request, suitabilityReport } = useAppSelector(state => state.persisted.savingsAgreement);
  const { data: customerInfo } = useCustomerInfo();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const data = request.agreementData;
  const savingCalculationsRequest = useAppSelector(state => state.persisted.savingCalculations.request);
  const { submitCreateAgreement } = useSubmitCreateAgreement();
  const { submitFundOrders } = useSubmitOrderFund();
  const startUpAgreementRequest = useMemo(
    () => Helpers.GetStartUpAgreementRequest(request, savingCalculations),
    [request, savingCalculations],
  );
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const cashAccount = request?.agreementData?.cashAccount ?? "";
  const portfolioId = request.portfolioId ?? "";
  const userName = `${customerInfo?.firstname} ${customerInfo?.lastName}`;

  const oneTimeDeposit = savingCalculationsRequest.oneTimeDeposit;
  const numOfMonthsStartupAmount = savingCalculationsRequest.numOfMonthsStartupAmount;
  const hasStartUpAgreement = oneTimeDeposit !== 0;

  const ActivateAgreementClick = async () => {
    setIsLoading(true);
    try {
      let kid = "";
      let fundOrderStatus = "";
      if (savingCalculationsRequest.monthlyAmount > 0) {
        const response = await submitCreateAgreement({
          ...request,
          agreementData: { ...request.agreementData, isSavingRobot: true },
          centerId: centerId,
        });
        dispatch(setResponse({ agreement: response }));
        kid = response.kid;
        fundOrderStatus = response.fundOrderStatus;
      }

      if (savingCalculations.numOfMonthsStartupAmount === 1 && savingCalculationsRequest.oneTimeDeposit > 0) {
        await submitFundOrders({
          portfolioId: request.portfolioId,
          funds: request.agreementData.funds.map(fund => ({
            isin: fund.isin,
            amount: CalculateStartUpAmountForFund(savingCalculationsRequest.oneTimeDeposit, 1, fund),
          })),
          cashAccount: request.agreementData.otherCashAccount
            ? request.agreementData.otherCashAccount
            : (request.agreementData.cashAccount as string),
          isSavingRobot: true,
          advisorCarriedOut: request.agreementData.advisorCarriedOut,
          advisorName: request.agreementData.advisorName,
        });
      }

      if (startUpAgreementRequest !== null && savingCalculations.numOfMonthsStartupAmount > 1) {
        await submitCreateAgreement(startUpAgreementRequest);
      }

      if (kid && fundOrderStatus === "V") {
        const totalAmount = request.agreementData.funds.reduce((acc, fund) => acc + fund.amount, 0);
        window.location.replace(
          `https://pvu.avtalegiro.no/ecsa/start?account=36263029539&name=Norne%20Securities%20AS&kid=${kid}&limit=${
            totalAmount * 2
          }&url=https://portal.norne.no/robot/18`,
        );
      } else {
        setIsLoading(false);
        router.push(`/robot/18`);
      }
    } catch (error) {
      setIsLoading(false);
      setError((error as Error).message);
    }
  };

  return (
    <MainBox>
      <div className={`flex flex-col ${hasStartUpAgreement && `lg:flex-row`}`}>
        {hasStartUpAgreement && (
          <AgreementSummary
            cashAccount={cashAccount}
            dateTo={format(new Date(GetEndDateForStartUpAgreement(numOfMonthsStartupAmount)), "dd.MM.yyyy")}
            portfolioId={portfolioId}
            isDefaultLanguage={isDefaultLanguage}
            userName={userName}
            header={numOfMonthsStartupAmount === 1 ? texts.PAGE_17_START_HEADER_ONE_TIME : texts.PAGE_17_START_HEADER}
            funds={MapFundsToStartUpAgreement(data.funds, oneTimeDeposit, numOfMonthsStartupAmount)}
            startUpAmount={oneTimeDeposit}
            startUpAgreement
            oneTime={numOfMonthsStartupAmount === 1}
          />
        )}
        <AgreementSummary
          cashAccount={cashAccount}
          dateTo={texts.PAGE_17_NO_DATE}
          portfolioId={portfolioId}
          isDefaultLanguage={isDefaultLanguage}
          userName={userName}
          header={data.adjustmentComment ?? ""}
          funds={data.funds}
        />
      </div>
      {suitabilityReport && <SuitabilityReport suitabilityReport={suitabilityReport} header={texts.PAGE_17_SUITABILITY_HEADER} />}
      <Footer
        isDefaultLanguage={isDefaultLanguage}
        router={router}
        onClick={() => ActivateAgreementClick()}
        isLoading={isLoading}
      />
      {error && <InfoBoxComponent texts={[error]} warning />}
    </MainBox>
  );
};
